import { isPromise } from "@faire/typescript";
import { ButtonOnClick } from "@faire/web/slate/Button/types";
import { useDelayedCallback } from "@faire/web/ui/hooks/useDelayedCallback";
import React, { useEffect, useRef, useState } from "react";

const SHOW_LOADING_SPINNER_DELAY = 1000;

// Copied from core/web/src/ui/hooks/useShouldShowSpinner.ts
/**
 * This hook is used to handle the loading state of the button.
 *
 * Apart from the initial states, it also handles the loading
 * state when the button is clicked and the handler returns a promise.
 *
 * It returns an object with the following properties:
 * - `handleClick`: The function to handle the click event.
 * - `shouldShowSpinner`: Whether the spinner should be shown, this has a delay after the click event.
 * - `isLoading`: Differently from `shouldShowSpinner`, which has a delay after the click event,
 *    `isLoading` is true when loading is true or when the click handler returns a promise.
 */
export const useShouldShowSpinner = (
  initialDisabled: boolean | undefined,
  initialLoading: boolean | undefined,
  initialOnClick?: ButtonOnClick
) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [loadingDelayExpired, setLoadingDelayExpired] = useState(false);

  const isMounted = useRef(true);

  const isDisabled = initialDisabled || initialLoading || internalLoading;

  const setDelayedLoading = useDelayedCallback(() => {
    setLoadingDelayExpired(true);
  });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * If provided onClick returns a promise this will automatically set the button into a loading state.
   */
  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    const promise = initialOnClick?.(e);

    if (isPromise(promise)) {
      setInternalLoading(true);
      const { cancel: cancelSetDelayedLoading } = setDelayedLoading(
        SHOW_LOADING_SPINNER_DELAY
      );

      try {
        await promise;
      } finally {
        cancelSetDelayedLoading();

        if (isMounted.current) {
          setInternalLoading(false);
          setLoadingDelayExpired(false);
        }
      }
    }
  };

  // only show spinner if loading prop passed or we are loading due to onClick promise and loading delay has passed.
  const shouldShowSpinner =
    initialLoading || (internalLoading && loadingDelayExpired);

  const isLoading = initialLoading || internalLoading;

  return { handleClick, isLoading, shouldShowSpinner };
};
